import { signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { DEFAULT_PAGE_QUERY } from '@constants/settings.constant';
import { handlerApiError, scanInfiniteScroll } from '@helpers/function.helper';
import {
  Observable,
  catchError,
  debounce,
  finalize,
  of,
  switchMap,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';

export interface IResponse<T> {
  code: number;
  data: T;
  success: boolean;
  message: string;
  timestamp: string;
}

export interface Page<T> {
  total: number;
  items: T[];
  error?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface PageQuery extends Record<string, any> {
  page: number;
  size: number;
  sort?: 'asc' | 'desc';
  sortField?: string;
}

export type EndpointConfig = {
  [key: string]: {
    path: string;
    mapper?: <T, R = Record<string, unknown>>(
      response: IResponse<R>
    ) => IResponse<T>;
  };
};

export abstract class InfiniteScrollComponent<
  T,
  Q extends PageQuery = PageQuery,
> {
  withDebounce = false;
  loading = signal(false);
  query = signal<Q>(this.getDefaultQuery());
  query$ = toObservable(this.query);
  data$ = this.query$.pipe(
    debounce(() => {
      if (this.withDebounce) return timer(300);
      return timer(0);
    }),
    tap(() => this.loading.set(true)),
    takeUntilDestroyed(),
    switchMap(query =>
      this.loadData(query).pipe(
        catchError(err => {
          handlerApiError(err, 'Tìm kiếm thất bại');
          return of({
            data: {
              items: [] as T[],
              total: 0,
              error: err,
            } satisfies Page<T>,
          } as IResponse<Page<T>>);
        }),
        finalize(() => this.loading.set(false))
      )
    ),
    withLatestFrom(this.query$),
    scanInfiniteScroll(),
    catchError(err => {
      handlerApiError(err, 'Tìm kiếm thất bại');
      return of({
        items: [],
        total: 0,
        error: err,
      } satisfies Page<T>);
    })
  );

  onScroll(data: Page<T>) {
    if (data.items.length >= data.total) {
      return;
    }
    this.query.update(value => ({
      ...value,
      page: value.page + 1,
    }));
  }

  protected reload() {
    this.query.update(value => ({
      ...value,
      page: 1,
    }));
  }

  abstract loadData(query: Q): Observable<IResponse<Page<T>>>;

  protected getDefaultQuery() {
    return DEFAULT_PAGE_QUERY as Q;
  }
}

export abstract class DropdownSearch<
  T,
  Q extends PageQuery = PageQuery,
> extends InfiniteScrollComponent<T, Q> {
  show = signal(false);

  open() {
    this.show.set(true);
  }

  close() {
    this.show.set(false);
  }

  abstract onSearch($event: KeyboardEvent, el: HTMLElement): void;
}

export type FileInfo = { file: File; url: string };

export enum NotificationType {
  shopSellItem = 'SHOP_SELL_ITEM',
}

export interface UserNotiNavigation {
  routing: string;
  params: Record<string, unknown>;
}
export interface UserNotification {
  text: string;
  createdDate: string;
  type: NotificationType;
  read: boolean;
  navigation?: UserNotiNavigation;
  id: number;
  objectId: number;
}

export type FormControlTypeConvert<T> = {
  [key in keyof T]: FormControl<T[key]>;
};

export interface Banner {
  id: number;
  image: string;
}
